import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { Tree, Spin, Dropdown, TreeProps, Tooltip, Badge, App } from 'antd'
import { useSearchParams } from 'react-router-dom'
import DebounceSearch from '@/components/debounceSearch'
import { useTranslation } from 'react-i18next'
import styles from './index.module.less'
import { formatTreeSelect } from './utils'
import TreeSelectModal from './treeSelectModal'
import EditModal from './editModal'
import { knowledgeDir } from '@/api/dictionary'
import MeddraCateTreeIcon from './meddraCateTreeIcon'

const MAX_LEVEL = 6

export enum Action {
  ADD_SIBLING = 'ADD_SIBLING',
  ADD_CHILDREN = 'ADD_CHILDREN',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  MOVE = 'MOVE',
  MERGE = 'MERGE',
}

interface DictTreeProps extends TreeProps {
  contextMenu:
    | {
        [key in Action]?: boolean
      }
    | boolean
  showRed?: boolean
  onSearch?: (keyword: string) => void
  onClick?: (nodeData: any) => void
  onEdit?: (values: any, action: Action | null) => void
  queryDictTree: (search: string) => any
  onLoad?: (node: any) => void
  isNeedHideCount?: boolean
}

const DictTree = (
  {
    contextMenu = false,
    showRed = false,
    onSearch,
    onClick,
    onEdit,
    queryDictTree,
    onLoad,
    isNeedHideCount = false,
    ...rest
  }: DictTreeProps,
  ref: any
) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const kowledgeIder = searchParams.get('kowledge')
  const isMedDRA = searchParams.get('isMedDRA')
  const { message, modal } = App.useApp()
  const actionRef = useRef(null)
  const [currentNode, setCurrentNode] = useState<any>(null)
  const treeInnerRef = useRef<any>(null)
  const editRef = useRef<any>(null)
  const moveRef = useRef<any>(null)
  const mergeRef = useRef<any>(null)
  const [treeData, setTreeData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')

  const fetchTreeData = async (withLoading: boolean = true) => {
    if (withLoading) {
      setLoading(true)
    }
    const result = await queryDictTree(searchValue || '')
    setTreeData(result)
    setLoading(false)
    typeof onLoad === 'function' && onLoad(result)
  }

  useEffect(() => {
    fetchTreeData()
  }, [searchValue])

  useImperativeHandle(ref, () => {
    return {
      refresh: (withLoading: boolean) => {
        fetchTreeData(withLoading)
      },
    }
  })

  const handleAction = async (record: any, action: any) => {
    actionRef.current = action.key
    setCurrentNode(record)
    switch (action.key) {
      case Action.ADD_SIBLING:
        editRef.current.open(
          {
            parentId: record.parentId,
          },
          { title: t('ikg.addSibling') }
        )
        break
      case Action.ADD_CHILDREN:
        editRef.current.open(
          {
            parentId: record.id,
          },
          { title: t('ikg.addChildren') }
        )
        break
      case Action.EDIT:
        editRef.current.open(
          {
            ...record,
            directoryId: record.id,
          },
          {
            title: t('ikg.edit'),
          }
        )
        break
      case Action.DELETE:
        modal.confirm({
          title: t('ikg.warn'),
          content: t('ikg.deleteConfirm'),
          centered: true,
          onOk: async () => {
            typeof onEdit === 'function' && (await onEdit(record, action.key))
            message.success(t('ikg.success', { action: t('ikg.delete') }))
          },
          transitionName: 'custom-slide-up',
        })
        break
      case Action.MOVE:
        moveRef.current.open({
          directoryId: record.id,
          keyPath: record.keyPath,
        })
        break
      case Action.MERGE:
        mergeRef.current.open({
          directoryId: record.id,
          keyPath: record.keyPath,
        })
        break
    }
  }

  const titleRender = (nodeData: any) => {
    let menuItems = [
      {
        label: t('ikg.addSibling'), // 新增同级目录
        key: Action.ADD_SIBLING,
        part: 0,
      },
      {
        label: t('ikg.addChildren'),
        key: Action.ADD_CHILDREN,
        part: 0,
      },
      {
        label: t('ikg.edit'),
        key: Action.EDIT,
        part: 1,
      },
      {
        label: t('ikg.delete'),
        key: Action.DELETE,
        part: 1,
      },
      {
        label: t('ikg.move'),
        key: Action.MOVE,
        part: 2,
      },
      {
        label: t('ikg.merge'),
        key: Action.MERGE,
        part: 2,
      },
    ]
    if (nodeData.count > 0) {
      menuItems = menuItems.filter((menu: any) => menu.key !== Action.DELETE)
    }
    // 是知识库暂时没有“合并和移动”
    if (kowledgeIder) {
      menuItems = menuItems.filter((menu: any) => (menu.key !== Action.MOVE && menu.key !== Action.MERGE))
    }
    if (nodeData.level >= MAX_LEVEL) {
      menuItems = menuItems.filter(
        (menu: any) => menu.key !== Action.ADD_CHILDREN
      )
    }
    if (!nodeData.parentId) {
      menuItems = [
        {
          label: t('ikg.addChildren'),
          key: Action.ADD_CHILDREN,
          part: 0,
        },
      ]
    }

    const title: string = nodeData.name || ''
    /**
     * 如果是meddra，需要在不同层级展示不同的图标
     */
    const level = nodeData.level;
    // !!!注意level为1时，代表字典类型，不展示图标，真正的目录从第二层开始
    const icon = isMedDRA && level !== 1 ? <MeddraCateTreeIcon level={level - 1} isForward={true} /> : null;

    const defaultItem = (
      <div title="" className={styles.treeNodeWrapper}>
        <div className={styles.treeNode}>
          {!!icon && <span className={styles.dictMeddraIcon}>{icon}</span>}
          {nodeData.parentId && (
            <span style={{ marginRight: 8 }}>{nodeData.code || ''}</span>
          )}

          <span>
            {/* {title.length > 10 ? `${title.substring(0, 10)}...` : title} */}
            {title}
          </span>
          {typeof nodeData.count === 'number' && !isNeedHideCount && (
            <span>（{nodeData.count}）</span>
          )}
        </div>
        {showRed && <Badge count={nodeData.redCount} />}
      </div>
    )
    const tooltipTitle = (
      <span>
        <span>{nodeData.title}</span>
        {typeof nodeData.count === 'number' && !isNeedHideCount && (
          <span>（{nodeData.count}）</span>
        )}
      </span>
    )
    let withContextMenu = !!contextMenu
    if (typeof contextMenu === 'object') {
      menuItems = menuItems.filter((menu: any) => {
        return contextMenu[menu.key as Action]
      })
      withContextMenu = menuItems.length > 0
    }
    if (!withContextMenu) {
      return (
        <Tooltip title={tooltipTitle} placement="top">
          {defaultItem}
        </Tooltip>
      )
    }

    const splitMenu = menuItems.reduce(
      (result: any, item: any) => {
        result[item.part].push(item)
        return result
      },
      [[], [], []]
    )
    return (
      <Tooltip title={tooltipTitle} placement="top">
        <Dropdown
          overlayClassName={styles.dictMenuDropdown}
          dropdownRender={() => {
            return (
              <div className={styles.dictMenu}>
                {splitMenu.map((menus: any, index: number) => {
                  if (menus.length === 0) return null
                  return (
                    <div className={styles.splitMenu} key={index}>
                      {menus.map((menu: any) => {
                        return (
                          <div
                            key={menu.key}
                            className={styles.menuItem}
                            onClick={(e) => {
                              e.stopPropagation()
                              const action = menuItems.find(
                                (n) => n.key === menu.key
                              )
                              handleAction(nodeData, action)
                            }}
                          >
                            {menu.label}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          }}
          trigger={['contextMenu']}
        >
          {defaultItem}
        </Dropdown>
      </Tooltip>
    )
  }

  const onFinish = async (values: any) => {
    typeof onEdit === 'function' && (await onEdit(values, actionRef.current))
  }

  const dataSource = formatTreeSelect({ tree: treeData })

  return (
    <>
      <DebounceSearch
        style={{ marginBottom: 8 }}
        placeholder={t('dict.searchTree')}
        onSearch={(val: any) => {
          setSearchValue(val)
        }}
      />
      <Spin spinning={loading} style={{ minHeight: 200 }}>
        {treeData.length > 0 && !loading && (
          <div className={styles.treeWrapper}>
            <div className={styles.treeInner} ref={treeInnerRef}>
              <Tree
                treeData={dataSource}
                titleRender={titleRender}
                onClick={(e, node) => {
                  typeof onClick === 'function' && onClick(node)
                }}
                defaultExpandedKeys={[dataSource[0].key]}
                onRightClick={({ event }: any) => {
                  event.preventDefault()
                }}
                {...rest}
              />
            </div>
          </div>
        )}
      </Spin>

      <EditModal onSubmit={onFinish} ref={editRef} />
      <TreeSelectModal
        type="move"
        ref={moveRef}
        onSubmit={async (values: any) => {
          if (typeof onEdit === 'function') {
            return await onEdit(values, Action.MOVE)
          } else {
            return null
          }
        }}
        disabledKey={currentNode?.key}
        queryDictTree={queryDictTree}
      />
      <TreeSelectModal
        type="merge"
        ref={mergeRef}
        onSubmit={async (values: any) => {
          if (typeof onEdit === 'function') {
            return await onEdit(values, Action.MERGE)
          } else {
            return null
          }
        }}
        disabledKey={currentNode?.key}
        queryDictTree={queryDictTree}
      />
    </>
  )
}

export default forwardRef(DictTree)
